import { Link } from "react-router-dom";
import { CATEGORY_META } from "@/lib/categories";
import { resolveAssetUrl } from "@/lib/api";
import { ArrowRight, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const statusStyle = {
  live: "bg-emerald-500/10 text-emerald-700 ring-1 ring-emerald-500/30",
  beta: "bg-amber-500/10 text-amber-700 ring-1 ring-amber-500/30",
  "coming-soon": "bg-blue-500/10 text-blue-700 ring-1 ring-blue-500/30",
  maintenance: "bg-slate-500/10 text-slate-700 ring-1 ring-slate-500/30",
};

export default function SystemCard({ system }) {
  const meta = CATEGORY_META[system.category];
  const Icon = meta?.icon;
  const featureHighlights = (system.core_features || []).slice(0, 3);

  return (
    <article
      className="card-elev bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col"
      data-testid={`system-card-${system.slug}`}
    >
      <Link to={`/systems/${system.slug}`} className="block relative aspect-[16/10] bg-slate-100 overflow-hidden">
        {system.thumbnail_url ? (
          <img
            src={resolveAssetUrl(system.thumbnail_url)}
            alt={system.name}
            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-400">
            {Icon ? <Icon className="w-10 h-10" /> : null}
          </div>
        )}
        {system.is_featured && (
          <div className="absolute top-3 left-3 px-2.5 py-1 bg-[#D4AF37] text-slate-900 text-[10px] uppercase tracking-[0.18em] font-semibold rounded-full">
            Featured
          </div>
        )}
      </Link>

      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
            {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
            <span>{system.category}</span>
          </div>
          <Badge className={`${statusStyle[system.status] || statusStyle.live} rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold border-0`}>
            {system.status}
          </Badge>
        </div>

        <Link to={`/systems/${system.slug}`}>
          <h3 className="mt-3 text-xl font-heading font-semibold text-slate-900 leading-snug hover:text-slate-700 transition-colors">
            {system.name}
          </h3>
        </Link>
        <p className="mt-2 text-slate-600 text-sm leading-relaxed line-clamp-3">{system.short_summary}</p>

        {featureHighlights.length > 0 && (
          <ul className="mt-4 space-y-1.5">
            {featureHighlights.map((f, i) => (
              <li key={i} className="text-xs text-slate-600 flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-[#D4AF37]" />
                <span className="line-clamp-1">{f}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 pt-4 border-t border-slate-100 flex items-center gap-2">
          <Link
            to={`/systems/${system.slug}`}
            className="flex-1 text-center text-sm font-medium px-4 py-2.5 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors inline-flex items-center justify-center gap-1.5"
            data-testid={`system-card-view-details-${system.slug}`}
          >
            View Details <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <Link
            to={`/systems/${system.slug}#demo`}
            className="flex-1 text-center text-sm font-medium px-4 py-2.5 rounded-full border border-slate-200 hover:border-slate-400 text-slate-700 transition-colors inline-flex items-center justify-center gap-1.5"
            data-testid={`system-card-open-demo-${system.slug}`}
          >
            Open Demo <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </article>
  );
}
